import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { FileText, Plus, Edit, Trash2, LogOut, User, Crown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom Logo Component (matching LandingPage)
const Logo = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Dashboard = () => {
  const { user, logout, authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageKey] = useState(Date.now());

  useEffect(() => {
    fetchResumes();
  }, []);

  const fetchResumes = async () => {
    try {
      const headers = authToken
        ? { Authorization: `Bearer ${authToken}` }
        : {};

      const response = await axios.get(`${API}/resumes`, {
        headers,
        withCredentials: true
      });
      setResumes(response.data);
    } catch (error) {
      toast.error('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteResume = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;

    try {
      const headers = authToken
        ? { Authorization: `Bearer ${authToken}` }
        : {};

      await axios.delete(`${API}/resumes/${resumeId}`, {
        headers,
        withCredentials: true
      });

      toast.success('Resume deleted successfully');
      fetchResumes();
    } catch (error) {
      toast.error('Failed to delete resume');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Navigation */}
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-200">
              <Logo className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl sm:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 hidden sm:block">Quick Hire</span>
          </div>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Action Buttons */}
            <div className="flex items-center gap-1 sm:gap-2">
              {user?.is_admin && (
                <Button
                  onClick={() => navigate('/admin')}
                  variant="ghost"
                  className="text-amber-600 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                  title="Admin Dashboard"
                >
                  <Crown className="h-5 w-5 sm:mr-2" />
                  <span className="hidden lg:inline font-medium">Admin</span>
                </Button>
              )}
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                title="Logout"
              >
                <LogOut className="h-5 w-5 sm:mr-2" />
                <span className="hidden lg:inline font-medium">Logout</span>
              </Button>
            </div>

            {/* Divider */}
            <div className="h-8 w-px bg-gray-200 hidden sm:block"></div>

            {/* Profile Section */}
            <div
              className="flex items-center gap-3 pl-2 group cursor-pointer relative"
              onClick={() => document.getElementById('profile-upload').click()}
              title="Click to update profile picture"
            >
              <div className="relative">
                {user?.picture ? (
                  <img
                    src={user.picture.startsWith('/uploads') ? `${BACKEND_URL}${user.picture}` : `${user.picture}?t=${imageKey}`}
                    alt={user.name}
                    className="w-10 h-10 sm:w-11 sm:h-11 rounded-full object-cover border-2 border-white shadow-md group-hover:shadow-lg group-hover:ring-2 group-hover:ring-blue-400 transition-all duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}

                <div
                  className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center border-2 border-white shadow-md text-blue-600 font-bold text-lg"
                  style={{ display: user?.picture ? 'none' : 'flex' }}
                >
                  {user?.name?.charAt(0) || 'U'}
                </div>

                {/* Edit Badge */}
                <div className="absolute -bottom-1 -right-1 bg-blue-600 text-white rounded-full p-1.5 shadow-sm opacity-0 group-hover:opacity-100 transition-all duration-300 scale-75 group-hover:scale-100">
                  <Edit className="w-3 h-3" />
                </div>
              </div>

              <div className="hidden md:block text-left transition-opacity">
                <p className="text-sm font-bold text-gray-800 leading-none mb-1 truncate max-w-[120px] lg:max-w-[160px]">{user?.name}</p>
                <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Update Photo</p>
              </div>

              <input
                type="file"
                id="profile-upload"
                className="hidden"
                accept="image/png, image/jpeg"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;

                  const formData = new FormData();
                  formData.append('file', file);

                  const toastId = toast.loading("Uploading...");

                  try {
                    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
                    await axios.post(`${API}/users/profile-picture`, formData, {
                      headers: { ...headers, 'Content-Type': 'multipart/form-data' },
                      withCredentials: true
                    });

                    toast.success('Profile picture updated!', { id: toastId });
                    setTimeout(() => window.location.reload(), 1000);
                  } catch (err) {
                    toast.error('Failed to upload image', { id: toastId });
                  }
                }}
              />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="dashboard-main">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">My Resumes</h1>
            <p className="text-gray-600">Create and manage your professional resumes</p>
          </div>
          <Button
            onClick={() => navigate('/resume/new')}
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-6"
            data-testid="create-resume-btn"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create New Resume
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : resumes.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No resumes yet</h3>
            <p className="text-gray-600 mb-6">Create your first professional resume now!</p>
            <Button
              onClick={() => navigate('/resume/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
              data-testid="empty-state-create-btn"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Resume
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resumes.map((resume) => (
              <Card
                key={resume.id}
                className="p-6 hover:shadow-xl transition-all border-2 border-transparent hover:border-blue-200 cursor-pointer"
                data-testid={`resume-card-${resume.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <FileText className="h-10 w-10 text-blue-600" />
                  <div className="flex space-x-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`/resume/${resume.id}`)}
                      data-testid={`edit-resume-${resume.id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteResume(resume.id)}
                      className="text-red-600 hover:text-red-700"
                      data-testid={`delete-resume-${resume.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{resume.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{resume.personal_info.full_name}</p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Template: {resume.template_id}</span>
                  <span>{new Date(resume.updated_at).toLocaleDateString()}</span>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
